import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import {
  Videocam,
  Camera,
  BarChart,
  Shield,
  Engineering
} from '@mui/icons-material';
import marketingTheme from '../themes/marketingTheme';
import WarehouseFlowVisualization from '../components/WarehouseFlowVisualization';

const LogisticsSolutions = () => {
  const applications = [
    { name: 'Time-and-Motion Analytics', icon: <Videocam />, key: 'time_and_motion' },
    { name: 'Ergonomic Analytics', icon: <Videocam />, key: 'ergonomic_analytics' },
    { name: 'Camera Analytics', icon: <Camera />, key: 'camera_analytics' },
    { name: 'Process Flow Comprehension', icon: <BarChart />, key: 'data_driven_decision_making_logistics' }
  ];

  const benefits = [
    { text: 'Use cameras to analyse your material flow.', icon: <Camera /> },
    { text: 'Uncover hidden insights about your supply chain using advanced analytics.', icon: <Engineering /> },
    { text: 'Proactive insights on ergonomics to improve safety and reduce injuries.', icon: <Shield /> }
  ];

  return (
    <Box sx={{ backgroundColor: '#24242a', minHeight: '100vh', pt: 12 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h1" 
            sx={{ 
              color: 'white', 
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              mb: 2 
            }}
          >
            Logistics Solutions
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: marketingTheme.palette.primary.main, 
              mb: 4,
              fontWeight: 'medium'
            }}
          >
            Optimizing supply chain productivity with cyber-physical systems and data-driven decision making.
          </Typography>
        </Box>

        {/* Solutions Overview */}
        <Box sx={{ 
          mb: 8,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
        }}>
          <Grid container spacing={6} alignItems="center" sx={{ p: 6, position: 'relative', zIndex: 1 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                position: 'relative',
                height: '350px',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                overflow: 'hidden'
              }}>
                {/* Placeholder Image */}
                <img 
                  // src="/images/logistics_data_example.jpg"
                  src="/images/warehouse_graph.jpg"
                  alt="Logistics Data Visualization" 
                  style={{ 
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '8px'
                  }} 
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ 
                color: 'white', 
                mb: 3,
                fontWeight: 600
              }}>
                Visualise Excess Motion in Your Supply Chain
              </Typography>
              <Typography variant="body1" sx={{ 
                color: 'rgba(255, 255, 255, 0.9)', 
                fontSize: '1.1rem',
                lineHeight: 1.7,
                mb: 4
              }}>
                Transform your warehouse operations with 3D spatial intelligence that tracks material flow, 
                identifies bottlenecks, and optimizes routing in real-time. See exactly where delays occur 
                and how to eliminate them for maximum throughput.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: '#1dd1a1' 
                    }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Flow Path Analysis
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: marketingTheme.palette.primary.main 
                    }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Bottleneck Detection
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: '#3498db' 
                    }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Throughput Optimization
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: '#e74c3c' 
                    }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Congestion Alerts
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* Capability Statement */}
        <Paper 
          sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.05)', 
            p: 4, 
            mb: 6,
            borderRadius: 2
          }}
        >
          <Typography variant="h4" sx={{ color: 'white', mb: 3 }}>
            Overview
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              lineHeight: 1.7,
              mb: 3
            }}
          >
            Intralogistics operations are complex and dynamic. The flow of goods and materials through a warehouse is a complex process, involving many different types of equipment and processes.
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              lineHeight: 1.7,
              mb: 3
            }}
          >
            Our solutions can help you enhance cost competitiveness, and improve resource flexibility, yielding better visibility over your operations. Enhanced awareness enables you to make smarter decisions which improve your bottom line.
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              lineHeight: 1.7,
              mb: 3
            }}
          >
            Our solutions help to infer supply chain bottlenecks and help you optimize throughput.
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              lineHeight: 1.7,
              mb: 3
            }}
          >
            We analyze visual data to identify patterns and provide useful information about motion and waiting wastes. We provide real-time alerts for adverse equipment or process operating conditions.
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              lineHeight: 1.7,
              mb: 4
            }}
          >
            Automatically recognise signals and events, enabling real-time adaptation to changing operational conditions. Drive improvement to productivity and safety.
          </Typography>
          
          <Box 
            component="img" 
            src="/images/warehouse_graph_cropped.jpg" 
            alt="Logistics Data" 
            sx={{ 
              width: '100%', 
              maxWidth: '800px',
              height: 'auto',
              borderRadius: 2,
              boxShadow: 3
            }} 
          />
        </Paper>

        {/* Benefits Section */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 4 }}>
            Benefits
          </Typography>
          <List>
            {benefits.map((benefit, index) => (
              <ListItem 
                key={index}
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  mb: 2,
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(237, 185, 46, 0.1)',
                    transform: 'translateX(8px)'
                  }
                }}
              >
                <ListItemIcon sx={{ color: marketingTheme.palette.primary.main, minWidth: '56px' }}>
                  {benefit.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={benefit.text}
                  sx={{ 
                    '& .MuiListItemText-primary': { 
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '1.1rem'
                    }
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Applications Section */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 4 }}>
            Applications
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              mb: 4 
            }}
          >
            See details about specific applications
          </Typography>
          <Grid container spacing={3}>
            {applications.map((application, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    height: '100%',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(237, 185, 46, 0.1)',
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 25px rgba(237, 185, 46, 0.3)'
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box sx={{ color: marketingTheme.palette.primary.main, mb: 2, fontSize: '3rem' }}>
                      {application.icon}
                    </Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: 'white',
                        fontWeight: 'medium'
                      }}
                    >
                      {application.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

{/* Call to Action */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Paper sx={{ 
            p: 6, 
            backgroundColor: 'rgba(237, 185, 46, 0.1)',
            border: '1px solid rgba(237, 185, 46, 0.3)'
          }}>
            <Typography variant="h4" sx={{ color: 'white', mb: 3, fontWeight: 600 }}>
              Ready to Transform Your Logistics Operations?
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              mb: 4,
              fontSize: '1.1rem'
            }}>
              Want to reduce costs, improve efficiency, and enhance safety in your logistics operations?
              Book a no obligation call to learn more.
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              <Grid item>
                <Button 
                  variant="contained" 
                  size="large"
                  sx={{ 
                    backgroundColor: marketingTheme.palette.primary.main,
                    color: '#24242a',
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#d4a326'
                    }
                  }}
                  href="/contact"
                >
                  Learn More
                </Button>
              </Grid>
              <Grid item>
                <Button 
                  variant="outlined" 
                  size="large"
                  sx={{ 
                    borderColor: marketingTheme.palette.primary.main,
                    color: marketingTheme.palette.primary.main,
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: '#d4a326',
                      backgroundColor: 'rgba(237, 185, 46, 0.1)'
                    }
                  }}
                  href="/contact"
                >
                  Book A Consult
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>

      </Container>
    </Box>
  );
};

export default LogisticsSolutions;
